import React from 'react';
import PropTypes from 'prop-types';

import { styled } from '@mui/system';
import Box from '@mui/material/Box';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Button from '@mui/material/Button';
import muiPropForwarding from 'styles/helpers/muiPropForwarding';
import Typography from '@mui/material/Typography';

import {
  getSectionNavMediumColor,
  getSectionNavLightColor
} from 'styles/helpers/getThemeStyles';

const StyledButton = styled(Button, {
  shouldForwardProp: muiPropForwarding('isHighlighted')
})(({ theme, isHighlighted }) => {
  const borderBottomColor = theme.palette?.sectionNav?.accent?.main;
  const notHighlightedColor = getSectionNavMediumColor(theme);
  const highlightedColor = theme.palette?.sectionNav?.link?.main;
  const hoverColor = theme.palette?.sectionNav?.link?.hover;

  return {
    '&.MuiButton-root': {
      minWidth: '59px',
      maxWidth: '59px',
      justifyContent: 'left',
      marginRight: '1px',
      paddingLeft: 0,
      paddingRight: 0,
      borderRadius: 0,
      paddingBottom: '5px',
      borderBottom: `2px ${
        isHighlighted ? borderBottomColor : 'transparent'
      } solid`,
      color: `${!isHighlighted ? notHighlightedColor : highlightedColor}`,
      '&:hover': {
        color: hoverColor
      },
      [theme.breakpoints.down('lg')]: {
        marginRight: '16px'
      }
    }
  };
});

const getIconFill = theme => getSectionNavLightColor(theme);

const StyledExpandMoreIcon = styled(ExpandMoreIcon)(({ theme }) => ({
  fill: getIconFill(theme)
}));

const StyledExpandLessIcon = styled(ExpandLessIcon)(({ theme }) => ({
  fill: getIconFill(theme)
}));

const MenuSectionBarMoreButton = ({ onClick, isExpanded, ...rest }) => {
  const { isHighlighted } = rest;

  const styles = { variant: 'sectionNav' };

  return (
    <StyledButton
      aria-controls="more-dropdown"
      aria-hidden="true"
      aria-haspopup="true"
      onClick={onClick}
      id="more-dropdown-btn"
      data-testid="more-dropdown-btn"
      tabIndex={-1}
      isHighlighted={isHighlighted}
    >
      <Box
        component="span"
        sx={{
          minWidth: '65px',
          display: 'flex',
          alignItems: 'flex-end'
        }}
      >
        <Typography
          component="span"
          sx={theme => ({
            textTransform: 'capitalize',
            position: 'relative',
            top: '-1px',
            fontSize: 16
          })}
          {...styles}
        >
          More
        </Typography>
        <Box
          sx={{
            display: 'inline-flex',
            padding: 0,
            margin: '0 -1px 0 0',
            position: 'relative'
          }}
        >
          {isExpanded ? <StyledExpandLessIcon /> : <StyledExpandMoreIcon />}
        </Box>
      </Box>
    </StyledButton>
  );
};

MenuSectionBarMoreButton.propTypes = {
  onClick: PropTypes.func,
  isHighlighted: PropTypes.bool,
  isExpanded: PropTypes.bool
};

export default MenuSectionBarMoreButton;
